import React, { useState, useRef, useEffect } from 'react';
import Chart from 'chart.js/auto';
import './VideoCompare.css';

// Enhanced hash computation - Perceptual hash style
function computePerceptualHash(imageData, size = 8) {
  const pixels = imageData.data;
  const width = imageData.width;
  const height = imageData.height;
  
  // Convert to grayscale
  const grayscale = [];
  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    grayscale.push(0.299 * r + 0.587 * g + 0.114 * b);
  }
  
  // Resize to size x size using simple averaging
  const blockWidth = Math.floor(width / size);
  const blockHeight = Math.floor(height / size);
  const blocks = [];
  
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let sum = 0;
      let count = 0;
      
      for (let by = 0; by < blockHeight; by++) {
        for (let bx = 0; bx < blockWidth; bx++) {
          const px = x * blockWidth + bx;
          const py = y * blockHeight + by;
          if (px < width && py < height) {
            sum += grayscale[py * width + px];
            count++;
          }
        }
      }
      
      blocks.push(count > 0 ? sum / count : 0);
    }
  }
  
  // Compute median
  const sorted = [...blocks].sort((a, b) => a - b);
  const median = sorted[Math.floor(sorted.length / 2)];
  
  // Create hash
  return blocks.map((val) => (val > median ? 1 : 0));
}

function hammingDistance(a, b) {
  if (a.length !== b.length) return Infinity;
  return a.reduce((acc, bit, i) => acc + (bit !== b[i] ? 1 : 0), 0);
}

const extractFrameHashes = async (videoElementOrUrl, interval = 1.0, onProgress) => {
  return new Promise((resolve, reject) => {
    console.log('🎬 Starting frame extraction');
    
    let video;
    let shouldCleanup = false;
    
    // If we got a video element, use it; otherwise create a new one with the URL
    if (videoElementOrUrl instanceof HTMLVideoElement) {
      video = videoElementOrUrl;
      console.log('📹 Using existing video element');
    } else if (typeof videoElementOrUrl === 'string' && videoElementOrUrl) {
      video = document.createElement('video');
      video.src = videoElementOrUrl;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.style.position = 'absolute';
      video.style.left = '-9999px';
      video.style.visibility = 'hidden';
      document.body.appendChild(video);
      shouldCleanup = true;
      console.log('📹 Created new video element for URL');
    } else {
      reject(new Error('Invalid video: expected HTMLVideoElement or URL string'));
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) {
      if (shouldCleanup) video.remove();
      reject(new Error("Could not get canvas context"));
      return;
    }
    
    // Use larger size for better matching (64x64 for processing, then hash to 8x8)
    canvas.width = 64;
    canvas.height = 64;
    
    const hashes = [];
    let currentTime = 0;
    let duration = 0;
    let isProcessing = false;
    let timeoutId = null;
    
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (shouldCleanup && video) {
        video.srcObject = null;
        video.src = '';
        if (video.parentNode) video.remove();
      }
    };
    
    const processFrame = () => {
      if (isProcessing) return;
      isProcessing = true;
      
      if (currentTime >= duration) {
        console.log(`✅ Finished extracting ${hashes.length} frames`);
        cleanup();
        resolve(hashes);
        return;
      }
      
      try {
        video.currentTime = currentTime;
      } catch (err) {
        console.error('Error seeking video:', err);
        isProcessing = false;
        currentTime += interval;
        if (currentTime < duration) {
          timeoutId = setTimeout(processFrame, 50);
        } else {
          cleanup();
          resolve(hashes);
        }
      }
    };
    
    video.onseeked = () => {
      try {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const hash = computePerceptualHash(imageData);
        hashes.push({ time: currentTime, hash });
        
        if (onProgress) {
          onProgress(Math.min(100, (currentTime / duration) * 100));
        }
        
        isProcessing = false;
        currentTime += interval;
        
        if (currentTime < duration) {
          timeoutId = setTimeout(processFrame, 10);
        } else {
          console.log(`✅ Finished extracting ${hashes.length} frames`);
          cleanup();
          resolve(hashes);
        }
      } catch (err) {
        console.warn("Could not hash frame at", currentTime, err);
        isProcessing = false;
        currentTime += interval;
        if (currentTime < duration) {
          timeoutId = setTimeout(processFrame, 50);
        } else {
          cleanup();
          resolve(hashes);
        }
      }
    };
    
    // If video is already loaded, use it directly
    if (video.readyState >= 2 && video.duration) {
      duration = video.duration;
      console.log(`📹 Video already loaded. Duration: ${duration.toFixed(2)}s`);
      setTimeout(() => processFrame(), 100);
      return;
    }
    
    video.onloadedmetadata = () => {
      duration = video.duration;
      if (!duration || isNaN(duration) || duration <= 0) {
        cleanup();
        reject(new Error("Invalid video duration"));
        return;
      }
      console.log(`📹 Video metadata loaded. Duration: ${duration.toFixed(2)}s`);
      
      // Wait for video to be ready
      const checkReady = () => {
        if (video.readyState >= 2) { // HAVE_CURRENT_DATA
          console.log('✅ Video ready, starting frame extraction');
          timeoutId = setTimeout(() => processFrame(), 100);
        } else {
          timeoutId = setTimeout(checkReady, 50);
        }
      };
      checkReady();
    };
    
    video.onerror = (e) => {
      console.error('❌ Video load error:', e);
      console.error('Video error details:', {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src
      });
      cleanup();
      reject(new Error("Failed to load video. The file may be corrupted or in an unsupported format."));
    };
    
    video.onloadstart = () => {
      console.log('🔄 Video load started');
    };
    
    // If we created a new video, we need to trigger loading
    if (shouldCleanup && video.src) {
      // Video will start loading automatically
    }
  });
};

function matchFrames(hashesA, hashesB, maxDist = 10) {
  const matches = [];
  
  // Use a sliding window approach for better matching
  const windowSize = Math.min(10, Math.floor(hashesB.length / 4));
  
  for (let i = 0; i < hashesA.length; i++) {
    let best = { dist: Infinity, match: null, index: -1 };
    
    // Search in a window around the expected position
    const startIdx = Math.max(0, i - windowSize);
    const endIdx = Math.min(hashesB.length, i + windowSize);
    
    for (let j = startIdx; j < endIdx; j++) {
      const dist = hammingDistance(hashesA[i].hash, hashesB[j].hash);
      if (dist < best.dist) {
        best = { dist, match: hashesB[j], index: j };
      }
    }
    
    if (best.dist <= maxDist && best.match) {
      const delta = best.match.time - hashesA[i].time;
      matches.push({
        time: hashesA[i].time,
        timeB: best.match.time,
        delta,
        confidence: 1 - (best.dist / maxDist),
        indexA: i,
        indexB: best.index
      });
    }
  }
  
  if (!matches.length) return [];
  
  // Apply temporal smoothing - filter out outliers
  const smoothedMatches = [];
  if (matches.length > 3) {
    // Calculate median delta
    const deltas = matches.map(m => m.delta).sort((a, b) => a - b);
    const medianDelta = deltas[Math.floor(deltas.length / 2)];
    const mad = deltas.map(d => Math.abs(d - medianDelta)).sort((a, b) => a - b)[Math.floor(deltas.length / 2)];
    const threshold = 3 * mad; // 3x median absolute deviation
    
    matches.forEach(match => {
      if (Math.abs(match.delta - medianDelta) <= threshold) {
        smoothedMatches.push(match);
      }
    });
  } else {
    smoothedMatches.push(...matches);
  }
  
  return smoothedMatches;
}

const VideoCompare = () => {
  const [videoA, setVideoA] = useState(null);
  const [videoB, setVideoB] = useState(null);
  const [videoAUrl, setVideoAUrl] = useState(null);
  const [videoBUrl, setVideoBUrl] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(null);
  const [isSynced, setIsSynced] = useState(false);
  const [stats, setStats] = useState(null);
  
  const graphRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const videoRefA = useRef(null);
  const videoRefB = useRef(null);
  const playbackIntervalRef = useRef(null);
  
  const handleFileSelect = (e, setter, urlSetter, currentUrl) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('video/')) {
      // Revoke old URL if exists
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      const url = URL.createObjectURL(file);
      setter(file);
      urlSetter(url);
      setError(null);
      console.log('📁 File selected:', file.name, 'URL created:', url);
    } else {
      setError('Please select a valid video file');
    }
  };
  
  const handleDrop = (e, setter, urlSetter, currentUrl) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      // Revoke old URL if exists
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      const url = URL.createObjectURL(file);
      setter(file);
      urlSetter(url);
      setError(null);
      console.log('📁 File dropped:', file.name, 'URL created:', url);
    } else {
      setError('Please drop a valid video file');
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleCompare = async () => {
    if (!videoAUrl || !videoBUrl) {
      setError('Please select both videos first');
      return;
    }
    
    console.log('🔍 Starting video comparison...');
    setLoading(true);
    setProgress(0);
    setProgressText('Extracting frames from video A...');
    setError(null);
    setComparisonData([]);
    setStats(null);
    
    try {
      // Create fresh blob URLs for extraction - the display URLs may have been revoked
      // by React Strict Mode (dev double-mount) or HMR. Fresh URLs from File are guaranteed
      // valid for the duration of extraction.
      if (!videoA || !videoB) {
        throw new Error('Both videos must be selected');
      }

      const urlA = URL.createObjectURL(videoA);
      const urlB = URL.createObjectURL(videoB);

      try {
        console.log('📹 Processing video A...');
        const hashesA = await extractFrameHashes(urlA, 1.0, (pct) => {
          setProgress(pct / 2);
          setProgressText(`Extracting frames from video A... ${Math.round(pct)}%`);
        });
        console.log(`✅ Extracted ${hashesA.length} frames from video A`);

        setProgressText('Extracting frames from video B...');
        console.log('📹 Processing video B...');
        const hashesB = await extractFrameHashes(urlB, 1.0, (pct) => {
          setProgress(50 + pct / 2);
          setProgressText(`Extracting frames from video B... ${Math.round(pct)}%`);
        });
        console.log(`✅ Extracted ${hashesB.length} frames from video B`);

        setProgressText('Matching frames...');
        setProgress(90);
        console.log('🔗 Matching frames...');

        if (!hashesA.length || !hashesB.length) {
          throw new Error("Could not extract frames from one or both videos. Make sure the videos are valid and can be played.");
        }

        const matches = matchFrames(hashesA, hashesB, 10);
        console.log(`🎯 Found ${matches.length} matching frames`);

        if (!matches.length) {
          throw new Error("No matching frames found. Try videos with similar content or adjust the matching threshold.");
        }

        setProgress(100);
        setProgressText('Complete!');

        setComparisonData(matches);

        // Calculate stats
        const deltas = matches.map(m => m.delta);
        const avgDelta = deltas.reduce((a, b) => a + b, 0) / deltas.length;
        const minDelta = Math.min(...deltas);
        const maxDelta = Math.max(...deltas);

        setStats({
          matches: matches.length,
          avgDelta: avgDelta.toFixed(2),
          minDelta: minDelta.toFixed(2),
          maxDelta: maxDelta.toFixed(2),
          totalTimeA: hashesA[hashesA.length - 1].time.toFixed(1),
          totalTimeB: hashesB[hashesB.length - 1].time.toFixed(1)
        });

        console.log('✅ Comparison complete!', {
          matches: matches.length,
          avgDelta: avgDelta.toFixed(2)
        });

        setTimeout(() => {
          setProgress(0);
          setProgressText('');
        }, 2000);
      } finally {
        URL.revokeObjectURL(urlA);
        URL.revokeObjectURL(urlB);
      }
    } catch (err) {
      console.error('❌ Comparison error:', err);
      setError(err.message || 'An error occurred during comparison. Check the browser console for details.');
      setProgress(0);
      setProgressText('');
    } finally {
      setLoading(false);
    }
  };
  
  const handleClear = () => {
    setVideoA(null);
    setVideoB(null);
    if (videoAUrl) URL.revokeObjectURL(videoAUrl);
    if (videoBUrl) URL.revokeObjectURL(videoBUrl);
    setVideoAUrl(null);
    setVideoBUrl(null);
    setComparisonData([]);
    setStats(null);
    setError(null);
    setProgress(0);
    setProgressText('');
    setIsSynced(false);
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }
  };
  
  const handleSyncPlay = () => {
    if (!comparisonData.length || !videoRefA.current || !videoRefB.current) return;
    
    setIsSynced(true);
    const videoA = videoRefA.current;
    const videoB = videoRefB.current;
    
    // Find initial offset
    const firstMatch = comparisonData[0];
    videoA.currentTime = firstMatch.time;
    videoB.currentTime = firstMatch.timeB;
    
    videoA.play();
    videoB.play();
    
    // Sync playback
    playbackIntervalRef.current = setInterval(() => {
      if (!videoA.paused && !videoB.paused) {
        const currentTimeA = videoA.currentTime;
        // Find closest match
        const closestMatch = comparisonData.reduce((prev, curr) => {
          return Math.abs(curr.time - currentTimeA) < Math.abs(prev.time - currentTimeA) ? curr : prev;
        });
        
        if (closestMatch) {
          const expectedTimeB = closestMatch.timeB + (currentTimeA - closestMatch.time);
          if (Math.abs(videoB.currentTime - expectedTimeB) > 0.5) {
            videoB.currentTime = expectedTimeB;
          }
        }
      }
    }, 100);
  };
  
  const handleSyncPause = () => {
    setIsSynced(false);
    if (videoRefA.current) videoRefA.current.pause();
    if (videoRefB.current) videoRefB.current.pause();
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
  };
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
      }
      if (videoAUrl) URL.revokeObjectURL(videoAUrl);
      if (videoBUrl) URL.revokeObjectURL(videoBUrl);
    };
  }, [videoAUrl, videoBUrl]);
  
  // Update chart when comparison data changes
  useEffect(() => {
    if (!comparisonData.length || !graphRef.current) return;
    
    const ctx = graphRef.current.getContext('2d');
    if (!ctx) return;
    
    // Destroy previous chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }
    
    chartInstanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: comparisonData.map((d) => d.time.toFixed(1)),
        datasets: [
          {
            label: 'Time Difference (s)',
            data: comparisonData.map((d) => d.delta),
            borderColor: '#6a5cb8',
            backgroundColor: 'rgba(106, 92, 184, 0.1)',
            borderWidth: 2,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: '#6a5cb8',
            fill: true,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        onClick: (_, elements) => {
          if (!elements.length) return;
          const index = elements[0].index;
          const match = comparisonData[index];
          if (videoRefA.current) videoRefA.current.currentTime = match.time;
          if (videoRefB.current) videoRefB.current.currentTime = match.timeB;
        },
        plugins: {
          legend: {
            labels: { color: '#888' }
          },
          tooltip: {
            backgroundColor: '#1a1a1a',
            titleColor: '#e0e0e0',
            bodyColor: '#e0e0e0',
            borderColor: '#6a5cb8',
            borderWidth: 1,
            callbacks: {
              label: (context) => {
                const match = comparisonData[context.dataIndex];
                return [
                  `Time A: ${match.time.toFixed(2)}s`,
                  `Time B: ${match.timeB.toFixed(2)}s`,
                  `Delta: ${match.delta.toFixed(2)}s`,
                  `Confidence: ${(match.confidence * 100).toFixed(1)}%`
                ];
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Time in Video A (s)',
              color: '#888',
              font: { size: 14 }
            },
            ticks: { color: '#888' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Time Difference (s)',
              color: '#888',
              font: { size: 14 }
            },
            ticks: { color: '#888' },
            grid: { color: 'rgba(255, 255, 255, 0.1)' }
          }
        }
      }
    });
    
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [comparisonData]);
  
  return (
    <div className="video-compare-container">
      <div className="video-compare-header">
        <h1>Video Compare</h1>
        <p className="video-compare-subtitle">
          Compare two videos from different perspectives. Analyze frame matches and simulate a virtual race.
        </p>
      </div>
      
      {/* Video Drop Zones */}
      <div className="videos-container">
        <div
          className={`video-dropzone ${videoAUrl ? 'has-video' : ''}`}
          onDrop={(e) => handleDrop(e, setVideoA, setVideoAUrl, videoAUrl)}
          onDragOver={handleDragOver}
          onDragEnter={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('dragging');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('dragging');
          }}
        >
          <div className="video-dropzone-label">Video A</div>
          {videoAUrl ? (
            <video
              ref={videoRefA}
              src={videoAUrl}
              controls
              preload="metadata"
              style={{ width: '100%', maxHeight: '400px' }}
              onError={(e) => {
                console.error('❌ Video A load error:', e);
                setError('Failed to load video A. Please try a different file.');
              }}
              onLoadedMetadata={() => {
                console.log('✅ Video A metadata loaded');
              }}
            />
          ) : (
            <div className="video-dropzone-text">
              Drop video file here or{' '}
              <label htmlFor="file-input-a" style={{ color: '#6a5cb8', cursor: 'pointer', textDecoration: 'underline' }}>
                browse
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileSelect(e, setVideoA, setVideoAUrl, videoAUrl)}
                style={{ display: 'none' }}
                id="file-input-a"
              />
            </div>
          )}
        </div>
        
        <div
          className={`video-dropzone ${videoBUrl ? 'has-video' : ''}`}
          onDrop={(e) => handleDrop(e, setVideoB, setVideoBUrl, videoBUrl)}
          onDragOver={handleDragOver}
          onDragEnter={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('dragging');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('dragging');
          }}
        >
          <div className="video-dropzone-label">Video B</div>
          {videoBUrl ? (
            <video
              ref={videoRefB}
              src={videoBUrl}
              controls
              preload="metadata"
              style={{ width: '100%', maxHeight: '400px' }}
              onError={(e) => {
                console.error('❌ Video B load error:', e);
                setError('Failed to load video B. Please try a different file.');
              }}
              onLoadedMetadata={() => {
                console.log('✅ Video B metadata loaded');
              }}
            />
          ) : (
            <div className="video-dropzone-text">
              Drop video file here or{' '}
              <label htmlFor="file-input-b" style={{ color: '#6a5cb8', cursor: 'pointer', textDecoration: 'underline' }}>
                browse
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileSelect(e, setVideoB, setVideoBUrl, videoBUrl)}
                style={{ display: 'none' }}
                id="file-input-b"
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="message error">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}
      
      {/* Controls */}
      <div className="controls-section">
        <button
          className="compare-button"
          onClick={(e) => {
            e.preventDefault();
            console.log('🔘 Compare button clicked', { videoAUrl: !!videoAUrl, videoBUrl: !!videoBUrl, loading });
            handleCompare();
          }}
          disabled={loading || !videoAUrl || !videoBUrl}
          type="button"
        >
          {loading && <span className="video-compare-loading-spinner"></span>}
          {loading ? 'Analyzing...' : 'Compare Videos'}
        </button>
        <button
          className="clear-button"
          onClick={handleClear}
          disabled={loading}
          type="button"
        >
          Clear
        </button>
      </div>
      
      {/* Progress */}
      {loading && (
        <div className="progress-section">
          <div className="progress-text">{progressText} {progress > 0 && `${Math.round(progress)}%`}</div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      )}
      
      {/* Stats */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-label">Matches Found</div>
            <div className="stat-value">{stats.matches}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Avg Time Diff</div>
            <div className="stat-value">{stats.avgDelta}s</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Min/Max Diff</div>
            <div className="stat-value">{stats.minDelta}s / {stats.maxDelta}s</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Duration</div>
            <div className="stat-value">A: {stats.totalTimeA}s / B: {stats.totalTimeB}s</div>
          </div>
        </div>
      )}
      
      {/* Chart */}
      {comparisonData.length > 0 && (
        <div className="results-section">
          <div className="chart-container">
            <h3 className="chart-title">Time Difference Analysis</h3>
            <div className="chart-wrapper">
              <canvas ref={graphRef}></canvas>
            </div>
          </div>
        </div>
      )}
      
      {/* Sync Controls */}
      {comparisonData.length > 0 && (
        <div className="sync-controls">
          <button
            className={`sync-button ${isSynced ? 'active' : ''}`}
            onClick={isSynced ? handleSyncPause : handleSyncPlay}
          >
            {isSynced ? '⏸ Pause Sync' : '▶ Play Sync'}
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoCompare;
